// Angular
import { BehaviorSubject, Observable, Subscription, first, map, takeUntil } from 'rxjs';
import { Component, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

// Libraries

import { MatDialog } from '@angular/material/dialog';

import { TranslateService } from '@ngx-translate/core';
import { Actions, Select, Store, ofActionCompleted } from '@ngxs/store';

// Models
import { UserModel } from '../../../../shared/models';
import { AppCleanup } from '../../../../shared/models';
import { CompanyListModel } from '../../../company/models/company-view-model.model';
import { DistrictUserViewModel } from '../../../district/models/district-user-view.model';
import { CompanyUserToAddDto } from '../../../company/models/company-user-to-add-dto';
import { DistrictUserAddDto } from '../../../district/models/district-user-add-dto.model';
import { DynamicTableColumn } from '../../../../shared/models';
import { DynamicMenuButton } from '../../../../shared/models';
import { CompanyUserViewModel } from '../../../../shared/models';
import { CompanyUserToEditDto } from '../../../company/models/company-user-to-edit-dto';
import { UnitUserViewModel } from '../../../asset/unit/models/unit-user-view.model';
import { UnitUserAddDto } from '../../../asset/unit/models/unit-user-add-dto.model';
import { UnitViewModel } from '../../../asset/unit/models/unit-view.model';
import { DistrictViewModel } from '../../../district/models/district-view.model';
import { CompanyDetailViewModel } from '../../../company/models/company-detail-view.model';

// Validators
import { ConfirmPasswordValidator } from '../../../../shared/validators/confirm-password.validator';

// Services
import { UserService } from '../../services/user.service';
import { AuthService } from '../../../auth';
import { CompanyService } from '../../../company/services/company.service';
import { DistrictService } from '../../../district/services/district.service';
import { DialogService } from '../../../../shared/services/dialog.service';
import { CanComponentDeactivate } from '../../../../shared/guards';
import { CoreService } from '../../../../shared/services/core.service';
import { UnitService } from '../../../asset/unit/services/unit.service';
import { UtilityService } from '../../../../shared/services';

// Components
import { ChangePasswordAdminDialogComponent } from '../../../company/dialogs/change-password-admin-dialog/change-password-admin-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { DynamicSelectDialogComponent } from '../../../../shared/dialogs/dynamic-select-dialog/dynamic-select-dialog.component';

// Store
import { UserAuthState } from '../../../auth/state/user-auth.state';
import { UserState } from '../../state/user.state';
import { UserActions } from '../../state/user.action';
import { DistrictState } from '../../../district/state/district.state';
import { DistrictActions } from '../../../district/state/district.action';
import { UnitState } from '../../../asset/unit/state/unit.state';
import { CompanyState } from '../../../company/state/company.state';
import { CompanyActions } from '../../../company/state/company.action';

// Enums
import { StatusType } from '../../../../shared/enums';
import { Roles } from '../../../auth/models/roles.enum';

// Helpers
import { FormHelper, RegexHelper, UtilityHelper } from '../../../../shared/helpers';
import { UnitActions } from '../../../asset/unit/state/unit.action';

@Component({
	selector: 'user-edit',
	templateUrl: './user-edit.component.html',
	styleUrls: ['./user-edit.component.scss'],
})
export class UserEditComponent extends AppCleanup implements OnInit, OnDestroy, CanComponentDeactivate {
	@HostBinding('class') classes = 'component-container';
	componentTitle = '';
	@Select(UserAuthState.currentUser) currentUser$: Observable<UserModel>;
	@Select(UserState.isLoaded) usersLoaded$: Observable<boolean>;
	@Select(UserState.users) users$: Observable<UserModel[]>;

	@Select(CompanyState.isLoaded) companiesLoaded$: Observable<boolean>;
	@Select(CompanyState.companies) companies$: Observable<CompanyListModel[]>;
	companies: CompanyListModel[];

	@Select(DistrictState.isLoaded) districtsLoaded$: Observable<boolean>;
	@Select(DistrictState.districts) districts$: Observable<DistrictViewModel[]>;
	districts: DistrictViewModel[];

	@Select(UnitState.isLoaded) unitsLoaded$: Observable<boolean>;
	@Select(UnitState.units) units$: Observable<UnitViewModel[]>;
	units: UnitViewModel[];

	editModel: UserModel;
	oldModel: UserModel;
	form: FormGroup;
	editingForm = false;
	isAdmin = false;
	companyView = false;
	backLink = '/user';
	companyId: number;
	roles: RoleMenuItem[] = [
		{
			id: 1,
			name: 'Member',
			enabled: false,
		},
		{
			id: 2,
			name: 'Admin',
			enabled: false,
		},
		{
			id: 3,
			name: 'Super Admin',
			enabled: false,
		},
		{
			id: 6,
			name: 'Tenant Admin',
			enabled: false,
		},
	];
	disableRoles = false; // Disables role dropdown (if current user has lower role than the user being edited)
	protected districtItemsVar: BehaviorSubject<DistrictUserViewModel[]> = new BehaviorSubject<DistrictUserViewModel[]>(null);
	districtItems$ = this.districtItemsVar.asObservable();

	districtColumnList: DynamicTableColumn[] = [
		{
			name: 'district.name',
			display: 'Name',
			width: 1,
		},
	];
	districtMenuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'close',
			name: this.translateService.instant('ACTION.REMOVE_CAPITAL'),
			event: this.removeDistrictFromUser.bind(this),
		},
	];
	protected unitItemsVar: BehaviorSubject<UnitUserViewModel[]> = new BehaviorSubject<UnitUserViewModel[]>(null);
	unitItems$ = this.unitItemsVar.asObservable();
	unitColumnList: DynamicTableColumn[] = [
		{
			name: 'unit.description',
			display: 'Name',
			width: 1,
		},
	];
	unitMenuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'close',
			name: this.translateService.instant('ACTION.REMOVE_CAPITAL'),
			event: this.removeUnitFromUser.bind(this),
		},
	];
	protected companyItemsVar: BehaviorSubject<CompanyUserViewModel[]> = new BehaviorSubject<CompanyUserViewModel[]>(null);
	companyItems$ = this.companyItemsVar.asObservable();
	companyColumnList: DynamicTableColumn[] = [
		{
			name: 'company.name',
			display: 'Name',
			width: 1,
		},
	];
	companyMenuButtons: DynamicMenuButton[] = [
		{
			matIcon: 'close',
			name: this.translateService.instant('ACTION.REMOVE_CAPITAL'),
			event: this.removeCompanyFromUser.bind(this),
		},
	];
	showPassword: boolean = false;
	showConfirmPassword: boolean = false;
	hideTooltip: boolean = true;

	@HostListener('window:resize', ['$event'])
	onResize() {
		this.hideTooltip = window.innerWidth > 767;
	}

	constructor(
		private formBuilder: FormBuilder,
		public userService: UserService,
		private coreService: CoreService,
		private companyService: CompanyService,
		private districtService: DistrictService,
		private unitService: UnitService,
		private dialogService: DialogService,
		private router: Router,
		public dialog: MatDialog,
		private authService: AuthService,
		private route: ActivatedRoute,
		private translateService: TranslateService,
		private store: Store,
		private actions$: Actions,
		private utilityService: UtilityService
	) {
		super();
	}

	ngOnInit() {
		this.utilityService.simulateWindowResize();

		this.usersLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
			if (!isLoaded) {
				this.store.dispatch(new UserActions.GetAll({ showSpinner: false, showToast: true }));
			}
		});

		this.companiesLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
			if (!isLoaded) {
				this.store.dispatch(new CompanyActions.GetAll({ showSpinner: false, showToast: true }));
			} else {
				this.companies$.pipe(takeUntil(this.destroy)).subscribe((companies) => {
					this.companies = companies;
				});
			}
		});

		this.districtsLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
			if (!isLoaded) {
				this.store.dispatch(new DistrictActions.GetAll({ showSpinner: false, showToast: true }));
			} else {
				this.districts$.pipe(takeUntil(this.destroy)).subscribe((districts) => {
					this.districts = districts;
				});
			}
		});

		this.unitsLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
			if (!isLoaded) {
				this.store.dispatch(new UnitActions.GetAll({ showSpinner: false, showToast: true }));
			} else {
				this.units$.pipe(takeUntil(this.destroy)).subscribe((units) => {
					this.units = units;
				});
			}
		});

		if (this.router.url.includes('company-user')) {
			this.companyView = true;
		}

		this.authService
			.checkAccess([Roles.Admin, Roles.SuperAdmin], () => {
				return true;
			})
			.subscribe((isAdmin) => {
				this.isAdmin = isAdmin;
			});

		this.route.queryParams.pipe(takeUntil(this.destroy)).subscribe((queryParams) => {
			if (queryParams && queryParams.backLink) {
				this.backLink = queryParams.backLink;
			}
		});

		this.route.params.pipe(takeUntil(this.destroy)).subscribe((params) => {
			const id = Number(params.id);
			if (id && id > 0) {
				if (this.companyView) {
					this.getCompanyUser(params.companyId, params.id);
				} else {
					this.getUser(id);
				}
			} else {
				this.editingForm = true;
				this.componentTitle = 'Add user';
				this.editModel = new UserModel();
				this.oldModel = Object.assign({}, this.editModel);
				this.roleCheck();
				this.createForm();
			}
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	getUser(id: number) {
		this.users$.pipe(takeUntil(this.destroy)).subscribe((users) => {
			if (users) {
				let foundUser = users.find((user) => user.id === id);
				if (!foundUser) {
					this.editModel = new UserModel();
					this.oldModel = Object.assign({}, this.editModel);
					this.createForm();
					const message = `${this.translateService.instant('GENERIC.NOT_FOUND', { name: this.translateService.instant('MODULE.USER.TITLE_CAPITAL') })}`;
					this.coreService.toast({ message: message, type: StatusType.DANGER });
					this.router.navigate([`/user`]);
					return;
				}

				foundUser = UtilityHelper.deepCopy(foundUser);
				this.editModel = foundUser;

				if (!this.editModel.isActive) {
					this.router.navigate(['/user']);
					const message = `${this.translateService.instant('MODULE.USER.NOT_ACTIVE')}`;
					this.coreService.toast({ message: message, type: StatusType.WARNING });
				}
				this.oldModel = Object.assign({}, this.editModel);
				this.roleCheck();
				this.mapPrimaryRole();
				this.componentTitle = 'Edit user';
				this.districtItemsVar.next(this.editModel.districtUsers);
				this.companyItemsVar.next(this.editModel.companyUsers);
				this.unitItemsVar.next(this.editModel.unitUsers);
				this.createForm();
			}
		});
	}

	getCompanyUser(companyId: number, userId: number) {
		this.companyService
			.getCompanyUser(companyId, userId)
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.editModel = Object.assign({}, result.user);
					this.editModel.companyUser = result;
					this.mapPrimaryRole();

					this.componentTitle = 'Edit user - ' + this.editModel.firstName + ' ' + this.editModel.lastName + ' | ' + result.company?.name;
					this.oldModel = Object.assign({}, this.editModel);
					this.createForm();
				}
			});
	}

	onEdit(editingForm: boolean) {
		// This method keeps role dropdown disabled when the user should not have access to it. We need this since editing the form enables the form making all fields enabled
		if (editingForm && this.disableRoles) {
			setTimeout(() => {
				this.form?.controls['roleId'].disable();
			}, 1);
		}
	}

	submit() {
		if (!this.form.dirty) return;

		const controls = this.form.controls;

		if (this.form.invalid) {
			Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
			return;
		}

		this.markFormAsClean();

		this.prepareModel();

		this.updateModel();
	}

	markFormAsClean() {
		this.form.markAsPristine();
		this.form.markAsUntouched();
		this.form.updateValueAndValidity();
	}

	private prepareModel() {
		if (this.editModel.roles.includes(2) || this.editModel.roles.includes(3)) {
			this.editModel.companyIds = [];
		}

		const controls = this.form.controls;
		this.editModel.firstName = controls.firstName.value;
		this.editModel.lastName = controls.lastName.value;
		this.editModel.email = controls.email.value;
		this.editModel.username = controls.username.value;

		if (!this.editModel.id) {
			this.editModel.password = controls.password.value;
		}

		if (this.companyView) {
			this.editModel.companyUser.isAdmin = controls.isAdmin.value;
			this.editModel.companyUser.isApproved = controls.isApproved.value;
		} else {
			// this.editModel.isActive = controls.isActive.value;
			this.editModel.primaryRoleId = controls.roleId.value;
		}
	}

	private updateModel() {
		if (this.editModel.id) {
			// Edit
			if (this.companyView) {
				// Company user
				const companyUser: CompanyUserToEditDto = {
					id: this.editModel.companyUser?.id,
					companyId: this.editModel.companyUser?.companyId,
					userId: this.editModel.id,
					user: this.editModel,
					isAdmin: this.editModel.companyUser.isAdmin,
					isApproved: this.editModel.companyUser.isApproved,
				};
				this.store.dispatch(new UserActions.UpdateCompanyUser({ companyUser: companyUser, showSpinner: true, showToast: true }));
				let subscription: Subscription = this.actions$.pipe(ofActionCompleted(UserActions.UpdateCompanyUserSuccess)).subscribe((response) => {
					this.createForm();
					subscription.unsubscribe();
				});
			} else {
				// User
				this.store.dispatch(new UserActions.Update({ user: this.editModel, showSpinner: true, showToast: true }));
				let subscription: Subscription = this.actions$.pipe(ofActionCompleted(UserActions.UpdateSuccess)).subscribe((response) => {
					this.createForm();
					subscription.unsubscribe();
				});
			}
		} else {
			// Create
			this.store.dispatch(new UserActions.Create({ user: this.editModel, showSpinner: true, showToast: true }));
			this.actions$.pipe(ofActionCompleted(UserActions.CreateSuccess)).subscribe((response) => {
				let url = UtilityHelper.stripQueryParamsFromUrl(this.router.url);
				this.router.navigate([`/user/edit/${response.action.user.id}`], { queryParams: { backLink: url } });
			});
		}
	}

	private createForm() {
		if (!this.editModel.id) {
			this.form = this.formBuilder.group(
				{
					firstName: [this.editModel.firstName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
					lastName: [this.editModel.lastName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
					email: [
						this.editModel.email ?? '',
						Validators.compose([
							Validators.required,
							Validators.email,
							Validators.minLength(3),
							// https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
							Validators.maxLength(320),
						]),
					],
					phone: [this.editModel.phone ?? '', Validators.compose([Validators.pattern(RegexHelper.phoneNumber())])],
					username: [this.editModel.username ?? '', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
					password: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(100)])],
					confirmPassword: ['', Validators.compose([Validators.required, Validators.minLength(6), Validators.maxLength(100)])],
					roleId: [this.editModel.primaryRoleId ?? 1, Validators.required],
					// isActive: [
					// 	this.editModel.id ? this.editModel.isActive : true,
					// 	Validators.required,
					// ],
				},
				{
					validator: ConfirmPasswordValidator.MatchPassword,
				}
			);
		} else {
			if (!this.companyView) {
				this.form = this.formBuilder.group({
					firstName: [this.editModel.firstName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
					lastName: [this.editModel.lastName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
					email: [
						this.editModel.email ?? '',
						Validators.compose([
							Validators.required,
							Validators.email,
							Validators.minLength(3),
							// https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
							Validators.maxLength(320),
						]),
					],
					phone: [this.editModel.phone ?? '', Validators.compose([Validators.pattern(RegexHelper.phoneNumber())])],
					username: [this.editModel.username ?? '', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
					roleId: [this.editModel.primaryRoleId ?? 0, Validators.required],
					// isActive: [this.editModel.isActive ?? 0, Validators.required],
				});
			} else {
				this.form = this.formBuilder.group({
					firstName: [this.editModel.firstName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
					lastName: [this.editModel.lastName ?? '', Validators.compose([Validators.required, Validators.minLength(2), Validators.maxLength(100)])],
					email: [
						this.editModel.email ?? '',
						Validators.compose([
							Validators.required,
							Validators.email,
							Validators.minLength(3),
							// https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
							Validators.maxLength(320),
						]),
					],
					phone: [this.editModel.phone ?? '', Validators.compose([Validators.pattern(RegexHelper.phoneNumber())])],
					username: [this.editModel.username ?? '', Validators.compose([Validators.required, Validators.minLength(3), Validators.maxLength(100)])],
					isAdmin: [this.editModel.companyUser?.isAdmin ?? false],
					isApproved: [this.editModel.companyUser?.isApproved ?? false],
				});
			}
		}

		this.checkFormAccess();
	}

	reset() {
		this.editModel = Object.assign({}, this.oldModel);
		this.createForm();
		this.markFormAsClean();
	}

	checkFormAccess() {
		if (!this.editingForm) {
			this.form.disable();
		} else {
			this.form.enable();
		}
	}

	goToDistrictRoute(districtUser: DistrictUserViewModel) {
		this.router.navigate([`district/edit/${districtUser.districtId}`], {
			queryParams: { backLink: this.router.url },
		});
	}

	goToUnitRoute(unitUser: UnitUserViewModel) {
		this.router.navigate([`unit/${unitUser.unitId}/edit`], {
			queryParams: { backLink: this.router.url },
		});
	}

	goToCompanyRoute(companyUser: CompanyUserViewModel) {
		this.router.navigate([`company/edit/${companyUser.companyId}`], {
			queryParams: { backLink: this.router.url },
		});
	}

	goToCompanyUserRoute(companyUser: CompanyUserViewModel) {
		this.router.navigate([`company/${companyUser.companyId}/company-user/edit/${companyUser.userId}`], {
			queryParams: { backLink: this.router.url },
		});
	}

	modifyUserRoles(selectedOptions) {
		this.editModel.roles = [];
		selectedOptions.selected.forEach((option) => {
			this.editModel.roles.push(option.value);
		});
	}

	modifyUserCompanies(selectedOptions) {
		if (!(this.editModel.roles.includes(2) || this.editModel.roles.includes(3))) {
			this.editModel.companyIds = [];
			selectedOptions.selected.forEach((option) => {
				this.editModel.companyIds.push(option.value);
			});
		}
	}

	openCompanyAddDialog() {
		let companies = this.companies.filter((company: CompanyListModel) => {
			return company.isActive && !this.editModel.companyUsers.find((companyUser: CompanyUserViewModel) => companyUser.company.id === company.id);
		});

		let columnList: DynamicTableColumn[] = [
			{
				name: 'name',
				display: 'Name',
			},
		];

		const dialogRef = this.dialog.open(DynamicSelectDialogComponent, {
			data: {
				items: companies,
				columnList: columnList,
				name: 'Companies',
				multiselect: true,
			},
			width: '100%',
			maxWidth: '600px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.addCompanyToUser(result);
				}
			});
	}

	addCompanyToUser(companies: CompanyDetailViewModel[]) {
		const companyUsers: CompanyUserViewModel[] = [];

		companies.forEach((d) => {
			companyUsers.push({
				id: 0,
				userId: this.editModel.id,
				user: this.editModel,
				companyId: d.id,
				company: d,
				isAdmin: false,
				isApproved: true,
			});
		});

		this.editModel.companyUsers.push(...companyUsers);

		this.updateCompanyDataSource();

		const companyUsersToAdd: CompanyUserToAddDto = {
			userIds: [this.editModel.id],
			companyIds: companies.map((company) => company.id),
			isAdmin: false,
			isApproved: true,
		};

		this.companyService
			.addUsersToCompany(companyUsersToAdd)
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				result.forEach((r) => {
					this.editModel.companyUserIds.push(r.id);
					this.editModel.companyUsers.filter((cu) => cu.companyId === r.companyId)[0].id = r.id;
				});

				if (result.length > 1) {
					const message = `${this.translateService.instant('MODULE.COMPANY.MULTIPLE_ADDED_TO_USER')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				} else {
					const message = `${this.translateService.instant('MODULE.COMPANY.SINGLE_ADDED_TO_USER')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				}
			});
	}

	updateCompanyDataSource() {
		this.companyItemsVar.next(this.editModel.companyUsers);
	}

	removeCompanyFromUser(companyUser: CompanyUserViewModel) {
		const confirmModel: ConfirmDialogModel = {
			title: `${this.translateService.instant('ACTION.REMOVE_CAPITAL')} ${this.translateService.instant('MODULE.COMPANY.TITLE')}`,
			message: `${this.translateService.instant('GENERIC.CONFIRM_REMOVE_FROM', { name: this.translateService.instant('MODULE.COMPANY.TITLE'), from: this.translateService.instant('MODULE.USER.TITLE') })}`,
			action: this.translateService.instant('ACTION.REMOVE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					let index = this.editModel.companyUserIds.indexOf(companyUser.id, 0);

					if (index > -1) {
						this.editModel.companyUserIds.splice(index, 1);
					}

					index = this.editModel.companyUsers.indexOf(companyUser, 0);

					if (index > -1) {
						this.editModel.companyUsers.splice(index, 1);
					}

					this.updateCompanyDataSource();

					this.companyService
						.removeUserFromCompany(companyUser.id)
						.pipe(takeUntil(this.destroy))
						.subscribe(() => {
							const message = `${this.translateService.instant('MODULE.COMPANY.REMOVED_FROM_USER')}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						});
				}
			});
	}

	openDistrictAddDialog() {
		let districts = this.districts.filter((district: DistrictViewModel) => {
			return !this.editModel.districtUsers.find((districtUser: DistrictUserViewModel) => districtUser.districtId === district.id);
		});

		let columnList: DynamicTableColumn[] = [
			{
				name: 'name',
				display: 'Name',
			},
		];

		const dialogRef = this.dialog.open(DynamicSelectDialogComponent, {
			data: {
				items: districts,
				columnList: columnList,
				name: 'Districts',
				multiselect: true,
			},
			width: '100%',
			maxWidth: '600px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.addDistrictToUser(result);
				}
			});
	}

	addDistrictToUser(districts: DistrictViewModel[]) {
		const districtUsers: DistrictUserViewModel[] = [];

		districts.forEach((d) => {
			districtUsers.push({
				id: 0,
				userId: this.editModel.id,
				user: this.editModel,
				districtId: d.id,
				district: d,
			});
		});

		this.editModel.districtUsers.push(...districtUsers);

		this.updateDistrictDataSource();

		const districtUsersToAdd: DistrictUserAddDto = {
			userIds: [this.editModel.id],
			districtIds: districts.map((district) => district.id),
		};

		this.districtService
			.addUsersToDistrict(districtUsersToAdd)
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				result.forEach((r) => {
					this.editModel.districtUserIds.push(r.id);
					this.editModel.districtUsers.filter((du) => du.districtId === r.districtId)[0].id = r.id;
				});

				if (result.length > 1) {
					const message = `${this.translateService.instant('MODULE.DISTRICT.MULTIPLE_ADDED_TO_USER')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				} else {
					const message = `${this.translateService.instant('MODULE.DISTRICT.SINGLE_ADDED_TO_USER')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				}
			});
	}

	updateDistrictDataSource() {
		this.districtItemsVar.next(this.editModel.districtUsers);
	}

	removeDistrictFromUser(districtUser: DistrictUserViewModel) {
		const confirmModel: ConfirmDialogModel = {
			title: `${this.translateService.instant('ACTION.REMOVE_CAPITAL')} ${this.translateService.instant('MODULE.DISTRICT.TITLE')}`,
			message: `${this.translateService.instant('GENERIC.CONFIRM_REMOVE_FROM', { name: this.translateService.instant('MODULE.DISTRICT.TITLE'), from: this.translateService.instant('MODULE.USER.TITLE') })}`, // `Are you sure you want to remove ${unitUser.unit.description}?`,
			action: this.translateService.instant('ACTION.REMOVE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					let index = this.editModel.districtUserIds.indexOf(districtUser.id, 0);

					if (index > -1) {
						this.editModel.districtUserIds.splice(index, 1);
					}

					index = this.editModel.districtUsers.indexOf(districtUser, 0);

					if (index > -1) {
						this.editModel.districtUsers.splice(index, 1);
					}

					this.updateDistrictDataSource();

					this.districtService
						.deleteUserFromDistrict(districtUser.id)
						.pipe(takeUntil(this.destroy))
						.subscribe(() => {
							const message = `${this.translateService.instant('MODULE.DISTRICT.REMOVED_FROM_USER')}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						});
				}
			});
	}

	openUnitAddDialog() {
		let units = this.units.filter((unit: UnitViewModel) => {
			return !this.editModel.unitUsers.find((unitUser: UnitUserViewModel) => unitUser.unitId === unit.id);
		});

		let columnList: DynamicTableColumn[] = [
			{
				name: 'description',
				display: 'Name',
			},
			{
				name: 'district.name',
				display: 'Current District',
			},
		];

		const dialogRef = this.dialog.open(DynamicSelectDialogComponent, {
			data: {
				items: units,
				columnList: columnList,
				name: 'Units',
				multiselect: true,
			},
			width: '100%',
			maxWidth: '600px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					this.addUnitToUser(result);
				}
			});
	}

	addUnitToUser(units: UnitViewModel[]) {
		const unitUsers: UnitUserViewModel[] = [];

		units.forEach((u) => {
			unitUsers.push({
				id: 0,
				userId: this.editModel.id,
				user: this.editModel,
				unitId: u.id,
				unit: u,
			});
		});

		this.editModel.unitUsers.push(...unitUsers);

		this.updateUnitDataSource();

		const unitUsersToAdd: UnitUserAddDto = {
			userIds: [this.editModel.id],
			unitIds: units.map((unit) => unit.id),
		};

		this.unitService
			.addUsersToUnit(unitUsersToAdd)
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				result.forEach((r) => {
					this.editModel.unitUserIds.push(r.id);
					this.editModel.unitUsers.filter((du) => du.unitId === r.unitId)[0].id = r.id;
				});

				if (result.length > 1) {
					const message = `${this.translateService.instant('MODULE.UNIT.MULTIPLE_ADDED_TO_USER')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				} else {
					const message = `${this.translateService.instant('MODULE.UNIT.SINGLE_ADDED_TO_USER')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				}
			});
	}

	updateUnitDataSource() {
		this.unitItemsVar.next(this.editModel.unitUsers);
	}

	removeUnitFromUser(unitUser: UnitUserViewModel) {
		const confirmModel: ConfirmDialogModel = {
			title: `${this.translateService.instant('ACTION.REMOVE_CAPITAL')} ${this.translateService.instant('MODULE.UNIT.TITLE')}`,
			message: `${this.translateService.instant('GENERIC.CONFIRM_REMOVE_FROM', { name: this.translateService.instant('MODULE.UNIT.TITLE'), from: this.translateService.instant('MODULE.USER.TITLE') })}`, // `Are you sure you want to remove ${unitUser.unit.description}?`,
			action: this.translateService.instant('ACTION.REMOVE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					let index = this.editModel.unitUserIds.indexOf(unitUser.id, 0);

					if (index > -1) {
						this.editModel.unitUserIds.splice(index, 1);
					}

					index = this.editModel.unitUsers.indexOf(unitUser, 0);

					if (index > -1) {
						this.editModel.unitUsers.splice(index, 1);
					}

					this.updateUnitDataSource();

					this.unitService
						.deleteUserFromUnit(unitUser.id)
						.pipe(takeUntil(this.destroy))
						.subscribe(() => {
							const message = `${this.translateService.instant('MODULE.UNIT.REMOVED_FROM_USER')}`;
							this.coreService.toast({ message: message, type: StatusType.SUCCESS });
						});
				}
			});
	}

	canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
		if (!this.form.dirty) {
			return true;
		} else {
			const dialogRef = this.dialog.open(ConfirmDialogComponent, {
				data: {
					message: `You have unsaved changes, would you like to continue?`,
				},
				autoFocus: false,
			});

			return dialogRef.afterClosed().pipe(
				map((result) => {
					return result;
				}),
				first()
			);
		}
	}

	controlHasError(controlName: string, validationType: string): boolean {
		return FormHelper.controlHasError(this.form, controlName, validationType);
	}

	openChangePasswordDialog() {
		if (this.disableRoles) {
			const message = `${this.translateService.instant('MODULE.USER.NOT_AUTHORIZED_TO_CHANGE_PASSWORD')}`;
			this.coreService.toast({ message: message, type: StatusType.WARNING });
			return;
		}

		let dialogRef = this.dialog.open(ChangePasswordAdminDialogComponent, {
			data: {
				username: this.editModel.username,
			},
			width: '100%',
			maxWidth: '350px',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result) => {
				if (result) {
					const message = `${this.translateService.instant('MODULE.USER.PASSWORD_CHANGED')}`;
					this.coreService.toast({ message: message, type: StatusType.SUCCESS });
				}
			});
	}

	mapPrimaryRole() {
		this.editModel.primaryRoleId = this.editModel.roles.includes(6) ? 6 : this.editModel.roles.includes(3) ? 3 : this.editModel.roles.includes(2) ? 2 : this.editModel.roles.includes(1) ? 1 : 0;
	}

	roleCheck() {
		let isCurrentUserTenantAdmin = false;
		let isCurrentUserSuperAdmin = false;
		let isCurrentUserAdmin = false;

		let isUserTenantAdmin = false;
		let isUserSuperAdmin = false;
		let isUserAdmin = false;

		if (this.editModel.roles.includes(6)) {
			isUserTenantAdmin = true;
		} else if (this.editModel.roles.includes(3)) {
			isUserSuperAdmin = true;
		} else if (this.editModel.roles.includes(2)) {
			isUserAdmin = true;
		}

		this.authService
			.getHighestRole()
			.pipe(takeUntil(this.destroy))
			.subscribe((currentUserHighestRole) => {
				if (currentUserHighestRole === 6) {
					isCurrentUserTenantAdmin = true;
				} else if (currentUserHighestRole === 3) {
					isCurrentUserSuperAdmin = true;
				} else if (currentUserHighestRole === 2) {
					isCurrentUserAdmin = true;
					this.roles[1].enabled = true;
				}

				if (isCurrentUserTenantAdmin) {
					this.roles[0].enabled = true;
					this.roles[1].enabled = true;
					this.roles[2].enabled = true;
					this.roles[3].enabled = true;
				} else if (isCurrentUserSuperAdmin) {
					if (!isUserTenantAdmin) {
						this.roles[0].enabled = true;
						this.roles[1].enabled = true;
						this.roles[2].enabled = true;
					} else {
						this.disableRoles = true;
					}
				} else if (isCurrentUserAdmin) {
					if (!isUserTenantAdmin && !isUserSuperAdmin) {
						this.roles[0].enabled = true;
						this.roles[1].enabled = true;
					} else {
						this.disableRoles = true;
					}
				}
			});
	}
}

export class RoleMenuItem {
	id: number;
	name: string;
	enabled: boolean;
}
